"use client";

import React, { Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { QrCode, Key, Hash, User, Shuffle, FileText, Palette, Table, Sparkles } from "lucide-react";

// Import individual generator components
import QRCodeGeneratorTool from "./components/QRCodeGeneratorTool";
import PasswordGeneratorTool from "./components/PasswordGeneratorTool";
import HashGeneratorTool from "./components/HashGeneratorTool";
import NameGeneratorTool from "./components/NameGeneratorTool";
import RandomNumberGeneratorTool from "./components/RandomNumberGeneratorTool";
import LoremIpsumGeneratorTool from "./components/LoremIpsumGeneratorTool";
import ColorPaletteGeneratorTool from "./components/ColorPaletteGeneratorTool";
import MarkdownTableGeneratorTool from "./components/MarkdownTableGeneratorTool";
import UUIDGeneratorTool from "./components/UUIDGeneratorTool";

const TABS = ["qr", "password", "hash", "name", "random", "lorem", "colors", "table", "uuid"] as const;
type TabValue = (typeof TABS)[number];

const OVERVIEW_ITEMS: { id: TabValue; icon: React.ElementType; title: string; description: string }[] = [
  { id: "qr", icon: QrCode, title: "QR Code", description: "Generate QR codes for URLs and text" },
  { id: "password", icon: Key, title: "Password", description: "Generate secure passwords" },
  { id: "hash", icon: Hash, title: "Hash", description: "Generate cryptographic hashes" },
  { id: "name", icon: User, title: "Name", description: "Generate random names" },
  { id: "random", icon: Shuffle, title: "Random Number", description: "Generate random numbers" },
  { id: "lorem", icon: FileText, title: "Lorem Ipsum", description: "Generate placeholder text" },
  { id: "colors", icon: Palette, title: "Color Palette", description: "Generate color palettes" },
  { id: "table", icon: Table, title: "Markdown Table", description: "Generate markdown tables" },
  { id: "uuid", icon: Sparkles, title: "UUID", description: "Generate unique identifiers" },
];

function GeneratorsPageContent() {
  const [activeTab, setActiveTab] = useQueryParam<TabValue>("tab", {
    parse: (raw) => (raw ?? "qr") as TabValue,
    serialize: (v) => v ?? null,
    defaultValue: "qr",
    replace: true,
  });

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Generators</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Generate QR codes, passwords, hashes, names, and more in one place
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="space-y-6">
          <TabsList className="flex w-full flex-wrap gap-2 p-2 rounded-xl bg-muted/60 h-auto min-h-12 [&>button]:shrink-0 [&>button]:rounded-lg [&>button]:data-[state=active]:bg-background [&>button]:data-[state=active]:shadow-sm">
            {OVERVIEW_ITEMS.map(({ id, icon: Icon, title }) => (
              <TabsTrigger key={id} value={id} className="flex items-center gap-2 px-4 py-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span>{title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-h-[320px]">
            <TabsContent value="qr" className="mt-0 animate-in fade-in-0 duration-200">
              <QRCodeGeneratorTool />
            </TabsContent>
            <TabsContent value="password" className="mt-0 animate-in fade-in-0 duration-200">
              <PasswordGeneratorTool />
            </TabsContent>
            <TabsContent value="hash" className="mt-0 animate-in fade-in-0 duration-200">
              <HashGeneratorTool />
            </TabsContent>
            <TabsContent value="name" className="mt-0 animate-in fade-in-0 duration-200">
              <NameGeneratorTool />
            </TabsContent>
            <TabsContent value="random" className="mt-0 animate-in fade-in-0 duration-200">
              <RandomNumberGeneratorTool />
            </TabsContent>
            <TabsContent value="lorem" className="mt-0 animate-in fade-in-0 duration-200">
              <LoremIpsumGeneratorTool />
            </TabsContent>
            <TabsContent value="colors" className="mt-0 animate-in fade-in-0 duration-200">
              <ColorPaletteGeneratorTool />
            </TabsContent>
            <TabsContent value="table" className="mt-0 animate-in fade-in-0 duration-200">
              <MarkdownTableGeneratorTool />
            </TabsContent>
            <TabsContent value="uuid" className="mt-0 animate-in fade-in-0 duration-200">
              <UUIDGeneratorTool />
            </TabsContent>
          </div>
        </Tabs>

        <Card className="card-enhanced mt-8 rounded-xl overflow-hidden">
          <CardHeader>
            <CardTitle>All Generators</CardTitle>
            <CardDescription>
              Click a card to switch to that generator
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {OVERVIEW_ITEMS.map(({ id, icon: Icon, title, description }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`text-left p-4 rounded-xl border transition-all duration-200 flex flex-col items-center sm:items-start gap-3 hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    activeTab === id
                      ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                    style={{ color: activeTab === id ? "#2563eb" : undefined }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm">{title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function GeneratorsPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8"><div className="text-center">Loading...</div></div>}>
      <GeneratorsPageContent />
    </Suspense>
  );
}
